import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const ServiceRequestsManagementPage = () => {
  const { currentUser, token, authLoading } = useAuth();
  const navigate = useNavigate();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchServiceRequests = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('[ServiceRequestsManagement] Fetching with token:', token ? 'Present' : 'Missing');
      
      // Try multiple endpoints with cache-busting
      const endpoints = ['/api/service-requests/admin', '/api/service-requests'];
      let response;
      let lastError;
      
      for (const endpoint of endpoints) {
        try {
          console.log(`[ServiceRequestsManagement] Trying endpoint: ${endpoint}`);
          const cacheBuster = `?_t=${Date.now()}`;
          response = await fetch(`${endpoint}${cacheBuster}`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            credentials: 'include'
          });
          
          if (response.ok) {
            console.log(`[ServiceRequestsManagement] Success with endpoint: ${endpoint}`);
            break;
          } else {
            console.log(`[ServiceRequestsManagement] Failed with endpoint: ${endpoint}, status: ${response.status}`);
            lastError = await response.json();
          }
        } catch (err) {
          console.log(`[ServiceRequestsManagement] Error with endpoint: ${endpoint}`, err);
          lastError = err;
          continue;
        }
      }
      
      if (!response || !response.ok) {
        throw new Error(lastError?.message || 'Failed to fetch service requests from any endpoint');
      }
      
      console.log('[ServiceRequestsManagement] Response status:', response.status);
      const data = await response.json();
      console.log('[ServiceRequestsManagement] Data received:', data);
      
      // Handle different response formats
      let requests = [];
      if (data.status === 'success' && data.data && data.data.serviceRequests) {
        requests = data.data.serviceRequests;
      } else if (data.status === 'success' && data.data && Array.isArray(data.data)) {
        requests = data.data;
      } else if (Array.isArray(data)) {
        requests = data;
      } else if (data.serviceRequests && Array.isArray(data.serviceRequests)) {
        requests = data.serviceRequests;
      } else {
        console.warn('[ServiceRequestsManagement] Unexpected data structure:', data);
        requests = [];
      }
      
      console.log('[ServiceRequestsManagement] Processed requests:', requests);
      setServiceRequests(requests);
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching service requests:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/users', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      const potentialTechnicians = data.filter(user => 
        user.role === 'admin' || user.role === 'manager' || user.role === 'staff'
      );
      setUsers(potentialTechnicians);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager'))) {
      console.log('[ServiceRequestsManagement] Access denied, redirecting to login');
      navigate('/login');
    } else if (token && currentUser) {
      console.log('[ServiceRequestsManagement] Authorized user, fetching data');
      fetchServiceRequests();
      fetchUsers();
    }
  }, [authLoading, currentUser, token, navigate, fetchServiceRequests, fetchUsers]);

  const handleUpdateRequest = async (requestId, updateData) => {
    if (!token || !requestId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/service-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to update service request');
      }
      
      await fetchServiceRequests(); 
      console.log('Service request updated successfully');
      
    } catch (err) {
      setError(err.message);
      console.error('Error updating service request:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (requestId, newStatus) => {
    if (!requestId || !newStatus) return;
    handleUpdateRequest(requestId, { status: newStatus });
  };

  const handleAssignTechnician = (requestId, technicianId) => {
    if (!requestId) return;
    handleUpdateRequest(requestId, { assignedTo: technicianId || null });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Filter and search logic
  const filteredRequests = serviceRequests.filter(req => {
    const matchesFilter = filter === 'all' || req.status === filter;
    const matchesSearch = searchTerm === '' || 
      req.contactFullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.serviceType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.id.toString().includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  if (authLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Checking authentication...</div>
        </div>
      </div>
    );
  }

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager')) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Access denied.</strong> Admin or Manager role required.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-brand-text-dark">
            Service Requests Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and track all customer service requests
          </p>
        </div>
        <button 
          onClick={fetchServiceRequests}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 transition-colors"
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Status Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status:
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search:
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, service type or ID..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{serviceRequests.length}</div>
            <div className="text-sm text-gray-600">Total Requests</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {serviceRequests.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {serviceRequests.filter(r => r.status === 'in_progress').length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {serviceRequests.filter(r => r.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{filteredRequests.length}</div>
            <div className="text-sm text-gray-600">Filtered Results</div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Service Requests Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned Technician
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading && filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredRequests.length > 0 ? (
                filteredRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <Link 
                        to={`/admin/service-requests/${req.id}`} 
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        #{req.id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">
                          {req.customer ? req.customer.username : req.contactFullName || 'Guest'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {req.customer ? req.customer.email : req.contactEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {req.serviceType || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(req.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <select 
                        value={req.status || 'pending'}
                        onChange={(e) => handleStatusChange(req.id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(req.status)}`}
                        disabled={isLoading}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <select 
                        value={req.assignedTo || ''}
                        onChange={(e) => handleAssignTechnician(req.id, e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        disabled={isLoading}
                      >
                        <option value="">Unassigned</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.username} ({user.role})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link 
                        to={`/admin/service-requests/${req.id}`}
                        className="text-blue-600 hover:text-blue-900 ml-3"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm || filter !== 'all' ? 'No requests found with current filters.' : 'No service requests found.'}
                    {!isLoading && (
                      <div className="mt-2">
                        <button 
                          onClick={fetchServiceRequests}
                          className="text-blue-600 hover:text-blue-800 underline text-sm"
                        >
                          Try refreshing the data
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Debug information (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded text-sm">
          <strong>Debug Info:</strong>
          <ul className="mt-2">
            <li>Current User: {currentUser ? `${currentUser.username} (${currentUser.role})` : 'None'}</li>
            <li>Token: {token ? 'Present' : 'Missing'}</li>
            <li>Service Requests Count: {serviceRequests.length}</li>
            <li>Users Count: {users.length}</li>
            <li>Loading: {isLoading ? 'Yes' : 'No'}</li>
            <li>Error: {error || 'None'}</li>
            <li>Filter: {filter}</li>
            <li>Search: {searchTerm || 'None'}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestsManagementPage; 